import { FC } from 'react'

import {
  Button,
  Flex,
  IconArrowUpRight,
  Link,
  PreviewCard
} from '@sequencehq/core-components'

import { invoicePaths } from 'Invoices/invoices.router'
import { isProductionOrDemoEnv } from 'lib/environment/environment'
import { OpenQuickbooksSettingsAction } from '../../OpenQuickbooksSettingsAction'
import { RepushSyncErrorAction } from '../../RepushSyncErrorAction'
import { quickbooksPaymentUrl } from '../../integrationSyncErrorLink'
import { hasQuickbooksSettingsAction } from '../../quickbooksSyncErrorContent'
import { SyncErrorInvoiceSnapshot, SyncErrorTaskInput } from '../../types'
import { useSyncErrorRepush } from '../../useSyncErrorRepush'
import { ViewRecordAction } from '../../ViewRecordAction'

const TEST_ID = 'watchtower.notification.integrationSyncError.recordCard'

export const PaymentRecordCard: FC<{
  taskId: string
  input: SyncErrorTaskInput
}> = ({ taskId, input }) => {
  const repush = useSyncErrorRepush({ taskId, input })
  const invoices = paymentInvoices(input)
  const hasSettingsAction = hasQuickbooksSettingsAction(input)
  const hasExternalPaymentAction =
    input.externalService === 'QuickBooks_Online' &&
    input.direction === 'PULL' &&
    input.externalEntityType === 'PAYMENT' &&
    Boolean(input.externalId)
  const hasActions =
    hasSettingsAction ||
    Boolean(repush) ||
    invoices.length > 0 ||
    hasExternalPaymentAction

  if (!hasActions) return null

  const title =
    input.direction === 'PULL'
      ? `QuickBooks payment${input.externalId ? ` · ${input.externalId}` : ''}`
      : (invoices.at(0)?.invoiceNumber ?? 'Payment')

  return (
    <PreviewCard
      data-testid={TEST_ID}
      showBorder
      title={title}
      content={
        <Flex gap="8px" flexWrap="wrap" marginTop="8px">
          {hasSettingsAction && (
            <OpenQuickbooksSettingsAction
              data-testid={`${TEST_ID}.openQuickbooksSettings`}
            />
          )}
          {repush && (
            <RepushSyncErrorAction
              repush={repush}
              data-testid={`${TEST_ID}.repush`}
            />
          )}
          {invoices.map(invoice => (
            <ViewRecordAction
              key={invoice.invoiceId}
              to={invoicePaths.edit(invoice.invoiceId)}
              label={
                invoices.length > 1
                  ? `View invoice ${invoice.invoiceNumber ?? invoice.invoiceId}`
                  : 'View invoice'
              }
              data-testid={`${TEST_ID}.viewInvoice.${invoice.invoiceId}`}
            />
          ))}
          {hasExternalPaymentAction && input.externalId && (
            <Button
              as={Link.Unstyled}
              to={quickbooksPaymentUrl(
                input.externalId,
                isProductionOrDemoEnv()
              )}
              target="_blank"
              rel="noopener noreferrer"
              variant="secondary"
              leadingIcon={<IconArrowUpRight />}
              data-testid={`${TEST_ID}.viewQuickbooksPayment`}
            >
              View payment in QuickBooks
            </Button>
          )}
        </Flex>
      }
    />
  )
}

const paymentInvoices = (
  input: SyncErrorTaskInput
): SyncErrorInvoiceSnapshot[] => {
  const snapshot = input.entitySnapshot
  if (snapshot?.snapshotType !== 'PAYMENT') return []
  if (snapshot.affectedInvoice?.invoiceId) return [snapshot.affectedInvoice]
  if (snapshot.relatedInvoices?.length) {
    return snapshot.relatedInvoices.filter(
      (invoice, index, invoices) =>
        Boolean(invoice.invoiceId) &&
        invoices.findIndex(other => other.invoiceId === invoice.invoiceId) ===
          index
    )
  }
  return snapshot.invoiceId
    ? [{ invoiceId: snapshot.invoiceId, invoiceNumber: snapshot.invoiceNumber }]
    : []
}
